import {useState,useEffect} from "react";
import { motion,AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom"
import { Box, Flex, Text, Button } from "@chakra-ui/react"
import Logo from "./navigation/Logo"
import { Squash as Hamburger } from 'hamburger-react'
import {useLanguage} from "../LanguageContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const MenuItems = ({children, to = "/", onClick, ...rest}) => {
  const isLink = typeof to === "string" && to !== "";
  return (
    <Text
      mb={{ base: 8, sm: 0 }}
      onClick={onClick}
      display="block"
      cursor={onClick ? "pointer" : "default"}
      transition="color 0.2s ease-in-out"
      _hover={{ color: "blue.300" }}
      {...rest}
    >
      {isLink ? <Link to={to}>{children}</Link> : children}
    </Text>
  )
}

const useMenuScale = (minHeight = 700, verticalMargin = 32 * 2) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      const screenHeight = window.innerHeight;
      const availableHeight = screenHeight - verticalMargin;
      const newScale = Math.min(1, availableHeight / minHeight);
      setScale(newScale);
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [minHeight, verticalMargin]);

  return scale;
};

const MotionBox = motion(Box);

const Header = () => {
  
  const navigate = useNavigate();
  const [ menuOpen, setMenuOpen ] = useState(false);
  const [shrinkNav, setShrinkNav] = useState(false);
  const [show] = useState(true);
  const { language, toggleLanguage } = useLanguage();
  const isEnglish = language === "en";

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);
  const scale = useMenuScale(700, 32); // you can adjust the 700 based on layout size

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
  const checkAuth = async () => {
    try {
      const res = await axios.get('/api/auth/check'); // This should work
      setIsLoggedIn(res.data.authenticated);
      console.log("Auth check:", res.data.authenticated);
    } catch (err) {
      console.log("User is not authenticated");
      setIsLoggedIn(false);
    }
  };

  checkAuth();

  // Add event listener for login event
  const handleLoginEvent = () => {
    checkAuth();
  };

  window.addEventListener('userLoggedIn', handleLoginEvent);

  return () => {
    window.removeEventListener('userLoggedIn', handleLoginEvent);
  };
  }, []);

  useEffect(() => {
    const threshold = 5;
    let ticking = false;
  
    const handleScroll = () => {
      const scrollY = window.scrollY;
  
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (scrollY > threshold && !shrinkNav) {
            setShrinkNav(true);
          } else if (scrollY <= threshold && shrinkNav) {
            setShrinkNav(false);
          }
          ticking = false;
        });
  
        ticking = true;
      }
    };
  
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll)
  }, [shrinkNav]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);  

  const navPadding = {base: shrinkNav ? 8:10 ,lg:shrinkNav ? 4 : 8};
  const logoWidth = shrinkNav ? "80px" : "100px";

  const handleLogout = async () => {
    // call the logout endpoint to invalidate the cookie -- TODO test that when logout is cleared that the cookie time to live in 0
    try {
      await axios.post("/api/logout");
      setIsLoggedIn(false);  // update your state to reflect logout
      // Clear any other auth-related data if needed
      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      wrap="wrap"
      position = "sticky"
      top = "0"
      zIndex = "1000"
      w="100%"
      transition="padding 0.2s ease"
      p={navPadding} // dynamic padding based on shrink
      bg={["black", "black"]}
      color={["white", "white"]}
      display={{ base: "flex", lg: "flex" }}
    >
      {/* Desktop layout: logo and menu items side-by-side */}
      <Flex display={{ base: "none", lg: "flex" }} align="center" justify="space-between" w="100%">
        <Logo
          w={logoWidth}
          transition="all 0.2s ease-in-out"
          color={["primary.500", "primary.500"]}
          fontSize="xl"
        />

      <Box display={{ base: "block", lg: "none" }} onClick={toggleMenu}>
       <Hamburger/>
      </Box>

      <Box
        display={{ base: show ? "block" : "none", lg: "block" }}
        flexBasis={{ base: "100%", lg: "auto" }}
        h={{base:"100vh", lg: "0"}}
      >
        <Flex
          align="center"
          justify={{base:"space-around", lg: "space-between"}}
          direction={{ base: "column", lg: "row" }}
          p={[10,10,0,0]}
          width="100%"
          height="50%"
          gap={5}
        >
          <MenuItems to="/" fontSize={["lg", "xl", "xl", "xl"]}>{isEnglish ? "Home" : "Inicio"}</MenuItems>
          <MenuItems to="/faq" fontSize={["lg", "xl", "xl", "xl"]}>{isEnglish ? "FAQ" : "Preguntas Frecuentes"}</MenuItems>
          <MenuItems to="/contactUs" fontSize={["lg", "xl", "xl", "xl"]}>{isEnglish ? "Contact Us" : "Contáctanos"}</MenuItems>
          <MenuItems to="/terms" fontSize={["lg", "xl", "xl", "xl"]}>{isEnglish ? "Terms" : "Términos"}</MenuItems>
          <MenuItems to="/privacyPolicy" fontSize={["lg", "xl", "xl", "xl"]}>{isEnglish ? "Privacy Policy" : "Política de Privacidad"}</MenuItems>
          {isLoggedIn && ( <MenuItems to="/dashboard" fontSize={["lg", "xl", "xl", "xl"]}>{isEnglish ? "Dashboard" : "Dashboard"}</MenuItems> )}
          {!isLoggedIn && (
          <MenuItems to="/signup/create-account" fontSize={["lg", "xl", "xl", "xl"]}>
            <Button 
              size="sm"
              rounded="md"
              color={["black", "black"]}
              bg={["white"]}
              _hover={{
                bg: "blue.600",
                color: "white"
              }}
            >
              {isEnglish ? "Create Account" : "Crear Cuenta"}
            </Button>
          </MenuItems>
    )}
          {!isLoggedIn && (
          <MenuItems to="/login" fontSize={["lg", "xl", "xl", "xl"]}>
            <Button 
              size="sm"
              rounded="md"
              color={["black", "black"]}
              bg={["white"]}
              _hover={{
                bg: "blue.600",
                color: "white"
              }}
            >
              {isEnglish ? "Log In" : "Iniciar Sesión"}
            </Button>
          </MenuItems>
          )}
          {isLoggedIn && (
            <MenuItems fontSize={["lg", "xl", "xl", "xl"]}>
              <Button 
                onClick={handleLogout}
                size="sm"
                rounded="md"
                color={["black", "black"]}
                bg={["white"]}
                _hover={{
                  bg: "blue.600",
                  color: "white"
                }}
              >
                {isEnglish ? "Logout" : "Cerrar Sesión"}
              </Button>
            </MenuItems>
          )}
          <MenuItems to={null} fontSize={["lg", "xl", "xl", "xl"]}>
            <Button 
              onClick={toggleLanguage}
              size="sm"
              rounded="md"
              color={["black", "black"]}
              bg={["white"]}
              _hover={{
                bg: "blue.600",
                color: "white"
              }}
            >
              {language === "en" ? "Español" : "English"}
            </Button>
          </MenuItems>
      </Flex>
      </Box>
      </Flex>
      
      {/* Mobile Menu*/}
      <Box
        display={{ base: "block", lg: "none" }}
        position="absolute"
        top={navPadding/2}
        right={menuOpen ? "calc(1rem + 14px)" : "1rem"}
        zIndex={1050}
      >
        <Hamburger toggled={menuOpen} toggle={setMenuOpen} size={24}/>
      </Box>
      
      <AnimatePresence>
        {menuOpen && (
          <MotionBox
            initial={{y: "-100%", opacity: 0.7}}
            animate={{y: 0, opacity: 1 }}
            exit={{y: "-100%", opacity: 0.87}}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            position="fixed"
            top={0}
            left={0}
            w="100vw"
            h="100vh"
            bg="black"
            zIndex={1000}
            display="flex"
            justifyContent="center"
            alignItems="center"
            px={4}
          >
            <Box
              transform={`scale(${scale})`}
              transformOrigin="center"
              bg="black"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={6}
            >
              <Logo
                w="100px"
                transition="all 0.2s ease-in-out"
                color={["primary.500", "primary.500"]}
                fontSize={["xl", "xl", "xl", "xl"]}
              />

              {[
                { to: "/", text: isEnglish ? "Home" : "Inicio" },
                { to: "/faq", text: isEnglish ? "FAQ" : "Preguntas Frecuentes" },
                { to: "/contactUs", text: isEnglish ? "Contact Us" : "Contáctanos" },
                { to: "/terms", text: isEnglish ? "Terms" : "Términos" },
                { to: "/privacyPolicy", text: isEnglish ? "Privacy Policy" : "Política de Privacidad" },
                ...(isLoggedIn
                  ? [{ to: "/dashboard", text: "Dashboard" }]
                  : []),
                ].map(({ to, text }) => (
                <MenuItems key={to} to={to} onClick={closeMenu} fontSize="xl">
                  {text}
                </MenuItems>
              ))}

              {!isLoggedIn && (
              <MenuItems to="/signup/create-account" onClick={closeMenu}>
                <Button size="sm" rounded="md" bg="white" color="black"
                  _hover={{
                    bg: "blue.600",
                    color: "white"
                  }}
                >
                  {isEnglish ? "Create Account" : "Crear Cuenta"}
                </Button>
              </MenuItems>
              )}
              {!isLoggedIn && (
              <MenuItems to="/login" onClick={closeMenu}>
                <Button size="sm" rounded="md" bg="white" color="black"
                  _hover={{
                    bg: "blue.600",
                    color: "white"
                  }}
                >
                  {isEnglish ? "Log In" : "Iniciar Sesión"}
                </Button>
              </MenuItems>
              )}
              {isLoggedIn && (
              <MenuItems to="/logout" onClick={closeMenu}>
                <Button size="sm" rounded="md" bg="white" color="black"
                  _hover={{
                    bg: "blue.600",
                    color: "white"
                  }}
                >
                  {isEnglish ? "Logout" : "Cerrar Sesión"}
                </Button>
              </MenuItems>
              )}
              <MenuItems to={null}>
                <Button onClick = {toggleLanguage} size="sm" rounded="md" bg="white" color="black" 
                  _hover={{
                    bg: "blue.600",
                    color: "white"
                  }}
                >
                  {language === "en" ? "Español" : "English"}
                </Button>
              </MenuItems>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </Flex>
  );
};

export default Header